import { useState, useEffect } from 'react';

/**
 * The vocabulary both pipeline tabs are written in.
 *
 * Shared rather than duplicated because the two tabs are two readings of one
 * book: a figure coloured as an outstanding amount on one should not be a
 * different grey on the other, and a payment class should not change hue when
 * the reader changes tab.
 */

/**
 * Identity by payment class, fixed and never recycled.
 *
 * Three hues rather than three steps of one, because these are categories and
 * not magnitudes - BNPL is not "more" than Upfront. The brand blue goes to
 * Upfront as the largest and most familiar group; the other two sit well away
 * from it in both hue and lightness so the bars stay separable in print and
 * under the common colour deficiencies.
 */
export const CLASS_COLOR = {
  Upfront: '#2F7FA8',
  BNPL: '#C96A1E',
  PPA: '#5B8C72',
};

/**
 * Hue is the family, lightness the configuration.
 *
 * Which makes the colour carry information rather than merely telling series
 * apart: the two hybrids read as a pair against the two retrofits at a glance,
 * and the chart answers "did hybrids move differently from retrofits" without
 * anyone reading the legend.
 */
export const CONFIG_COLORS = ['#1F5E7D', '#7CB8D4', '#9C4A18', '#E0A070'];

export const INK = '#262B40';
/** Totals. Present, readable, and deliberately quieter than an outstanding. */
export const TOTAL_INK = '#6B7280';
export const NEUTRAL = '#667085';
export const FAINT = '#9AA3B2';
export const GRID = '#EEF1F5';
export const RECEIVED = '#2F7FA8';
export const OWED = '#B4531F';

export const money = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);
export const moneyShort = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (a >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${Math.round(v)}`;
};
export const pct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);

/**
 * Whether the viewport is too narrow for a wide money table.
 *
 * A breakpoint rather than a media query because the choice is which component
 * to render, not which styles to apply: a table of this width cannot be made
 * to fit a phone by styling it, and letting it scroll sideways hides the
 * columns the block exists to show.
 */
export const NARROW = 768;

export function useNarrow() {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth < NARROW,
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < NARROW);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}
